import { BusinessSettings } from "shared/types";
import { isManager, isSystemAdmin } from "shared/utils/role";

// Pembatasan Admin: the capabilities a Manager can take away from plain
// Admins. Mirrors spott-server src/util/admin-restrictions.ts — keep the
// enum values and the field mapping identical in both files.
export enum AdminCapability {
  PackageVoidExpiry = "package_void_expiry",
  CreditAdjustment = "credit_adjustment",
  CatalogPricing = "catalog_pricing",
  // The three Pengaturan launcher sections, one toggle each (Andrew,
  // 2026-09-03: one umbrella "business settings" toggle was too coarse).
  BookingPolicy = "booking_policy",
  PromoReward = "promo_reward",
  OperationalSettings = "operational_settings",
  FinancialAnalytics = "financial_analytics",
  // A 6-char login code or a magic link IS a working sign-in for that person's
  // account, so seeing one is the same power as holding it. Split per surface
  // because the two leak different things: a client credential opens that
  // client's bookings, an instructor credential opens a roster showing every
  // client's phone number. Restricting the view also restricts regenerating
  // for that surface (the server gates the routes on both).
  LoginCredentialsClient = "login_credentials_client",
  LoginCredentialsInstructor = "login_credentials_instructor",
  LoginLinkRegenerate = "login_link_regenerate",
  BookingRefund = "booking_refund",
}

export type AdminRestrictionField =
  | "admin_restrict_package_void_expiry"
  | "admin_restrict_credit_adjustment"
  | "admin_restrict_catalog_pricing"
  | "admin_restrict_booking_policy"
  | "admin_restrict_promo_reward"
  | "admin_restrict_operational_settings"
  | "admin_restrict_financial_analytics"
  | "admin_restrict_login_credentials_client"
  | "admin_restrict_login_credentials_instructor"
  | "admin_restrict_login_link_regenerate"
  | "admin_restrict_booking_refund";

export const ADMIN_RESTRICTION_FIELD: Record<AdminCapability, AdminRestrictionField> = {
  [AdminCapability.PackageVoidExpiry]: "admin_restrict_package_void_expiry",
  [AdminCapability.CreditAdjustment]: "admin_restrict_credit_adjustment",
  [AdminCapability.CatalogPricing]: "admin_restrict_catalog_pricing",
  [AdminCapability.BookingPolicy]: "admin_restrict_booking_policy",
  [AdminCapability.PromoReward]: "admin_restrict_promo_reward",
  [AdminCapability.OperationalSettings]: "admin_restrict_operational_settings",
  [AdminCapability.FinancialAnalytics]: "admin_restrict_financial_analytics",
  [AdminCapability.LoginCredentialsClient]:
    "admin_restrict_login_credentials_client",
  [AdminCapability.LoginCredentialsInstructor]:
    "admin_restrict_login_credentials_instructor",
  [AdminCapability.LoginLinkRegenerate]: "admin_restrict_login_link_regenerate",
  [AdminCapability.BookingRefund]: "admin_restrict_booking_refund",
};

// Page order on Pembatasan Admin and the order the launcher tile counts in.
export const CAPABILITY_ORDER: AdminCapability[] = [
  AdminCapability.PackageVoidExpiry,
  AdminCapability.CreditAdjustment,
  AdminCapability.CatalogPricing,
  AdminCapability.BookingPolicy,
  AdminCapability.PromoReward,
  AdminCapability.OperationalSettings,
  AdminCapability.FinancialAnalytics,
  // Ahead of LoginLinkRegenerate: the broader restriction reads first and the
  // narrower one it supersedes sits directly under it.
  AdminCapability.LoginCredentialsClient,
  AdminCapability.LoginCredentialsInstructor,
  AdminCapability.LoginLinkRegenerate,
  AdminCapability.BookingRefund,
];

/**
 * Whether the signed-in user may NOT perform `cap`. Managers and system
 * admins are never restricted. Fails CLOSED while settings are missing (still
 * loading, or the request failed): a control that briefly shows disabled and
 * then enables beats one that is clickable and then answers 403. A server
 * that predates the flags returns no field, which reads as open.
 */
export const isCapabilityRestricted = (
  roles: string[] | undefined,
  settings: BusinessSettings | undefined,
  cap: AdminCapability,
): boolean => {
  if (isManager(roles) || isSystemAdmin(roles)) return false;
  if (!settings) return true;
  return Boolean(settings[ADMIN_RESTRICTION_FIELD[cap]]);
};
